import { AxiosError } from "axios";
import { useSetAtom } from "jotai";
import { useContext, useState } from "react";

import { DataContext } from "@/provider/DataContextProvider";
import { checkCscLogin } from "@/services/api";
import { BasicCustomerDataTwoFactorMethod } from "@/services/model";
import { CUSTOMER_AUTHORIZATION, TOKEN_URL } from "@/utils/apiUrls";
import { setAuthAtom, showsSessionExpiredModalAtom } from "@/utils/atoms";
import { createReferenceId } from "@/utils/initializeSentry";

/**
 * This hook provides authentication functionality for logging in customers using username/password credentials. It handles the authentication process, token management, and state updates
 *
 * This serves to remove the older login flow (see Ticket 839)
 */
interface Props {
  onSuccess?: () => void;
  onError?: (error: AxiosError) => void;
}

const extractErrorMessage = (error: unknown): string => {
  if (error instanceof AxiosError) {
    const data = error.response?.data as
      | {
          message?: string;
          error_description?: string;
          error?: string;
        }
      | undefined;

    return (
      data?.message || data?.error_description || data?.error || error.message
    );
  }

  if (error instanceof Error) {
    return error.message;
  }

  return "Unknown error";
};

export const useCustomerLogin = (options?: Props) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<AxiosError | null>(null);
  const setShowSessionExpiredModal = useSetAtom(showsSessionExpiredModalAtom);
  const setAuth = useSetAtom(setAuthAtom);
  const { setDataContext } = useContext(DataContext);

  const [twoFactorGrantToken, setTwoFactorGrantToken] = useState();
  const [shouldShowVerifyEmailModal, setShouldShowVerifyEmailModal] =
    useState(false);
  const [shouldShowTwoFactorModal, setShouldShowTwoFactorModal] =
    useState(false);
  const [twoFactorMethod, setTwoFactorMethod] =
    useState<BasicCustomerDataTwoFactorMethod>(
      BasicCustomerDataTwoFactorMethod.STANDARD,
    );

  const referenceId = createReferenceId();

  const loginAsCustomer = async ({
    email,
    password,
    skipEmailVerificationCheck,
  }: {
    email: string;
    password: string;
    skipEmailVerificationCheck?: boolean;
  }) => {
    setIsLoading(true);
    setError(null);

    checkCscLogin(email, {
      headers: {
        "X-Reference-Id": referenceId,
        "X-Skip-Auth": "true",
      },
    })
      .then(async (response) => {
        setDataContext((prev) => ({
          ...prev,
          isEmailVerified: !!response.emailVerified,
        }));

        // Explicitly check for 'emailVerified === false' as if email adress is not yet taken it will not be sent by backend - so would be undefined
        if (!skipEmailVerificationCheck && response.emailVerified === false) {
          setShouldShowVerifyEmailModal(true);
          setIsLoading(false);

          return; // STOP normal login here
        }

        try {
          const referenceId = createReferenceId();
          const formData = new URLSearchParams();
          formData.append("grant_type", "password");
          formData.append("username", email);
          formData.append("password", password);

          const tokenResponse = await fetch(TOKEN_URL.toString(), {
            method: "POST",
            // credentials: "include", // required for Safari
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
              Authorization: CUSTOMER_AUTHORIZATION,
              "X-Reference-Id": referenceId,
            },
            body: formData,
          });

          if (!tokenResponse.ok) {
            const errorData = await tokenResponse.json().catch(() => ({}));

            const error = new AxiosError(
              errorData.message ||
                errorData.error_description ||
                "Login failed",
              errorData.error_code || undefined,
              {
                method: "POST",
                url: tokenResponse.url,
                headers: Object.fromEntries(tokenResponse.headers) as any,
              },
              null,
              {
                // Response
                data: errorData,
                status: tokenResponse.status,
                statusText: tokenResponse.statusText,
                headers: {},
                config: {} as any,
              },
            );

            setError(error);
            throw error;
          }

          const tokenData = await tokenResponse.json();

          // Response returns twoFactorGrantToken means user has 2fa and we want to set the (temp) token
          if (tokenData.twoFactorGrantToken) {
            setTwoFactorGrantToken(tokenData.twoFactorGrantToken);
          } else if (!tokenData.access_token || !tokenData.refresh_token) {
            throw new Error("Invalid token response");
          }
          // If neither twoFactorGrantToken nor Error applied it means we need to set user as logged in
          else {
            setAuth({
              accessToken: tokenData.access_token,
              refreshToken: tokenData.refresh_token,
              isAnonymous: false,
            });

            // Clear session expired modal state on successful login
            setShowSessionExpiredModal(undefined);

            if (options?.onSuccess) {
              options.onSuccess();
            }
          }

          if (
            response.method &&
            response.method !== BasicCustomerDataTwoFactorMethod.STANDARD
          ) {
            setTwoFactorMethod(response.method);
            setShouldShowTwoFactorModal(true);
          }

          return tokenData;
        } catch (error) {
          setError(error as AxiosError);
          const errorMessage = extractErrorMessage(error);
          const isCodeAlreadySent = errorMessage.includes(
            "Der code wurde bereits versendet",
          );

          // Avoid showing a generic login error toast for this known 2FA transition.
          if (options?.onError && !isCodeAlreadySent) {
            options.onError(error as AxiosError);
          }

          // TODO: refactor when backend sends unified error codes
          if (isCodeAlreadySent) {
            // If 2fa SMS Code was already sent we open 2fa modal again to enable user to login
            setShouldShowTwoFactorModal(true);
          } else {
            // If login failed 2fa modal should not be shown
            setShouldShowTwoFactorModal(false);

            throw new Error("Login failed");
          }
        } finally {
          setIsLoading(false);
        }
      })
      .catch((error) => {
        // Only call error handler if this is an error from checkCscLogin
        // Errors from token fetch are already handled in the inner catch
        if (options?.onError && (error as Error).message !== "Login failed") {
          options.onError(error as AxiosError);
        }
        // If login failed 2fa modal should not be shown
        setShouldShowTwoFactorModal(false);
        setIsLoading(false);
      });
  };

  return {
    loginAsCustomer,
    isLoading,
    setIsLoading,
    error,
    shouldShowTwoFactorModal,
    setShouldShowTwoFactorModal,
    twoFactorGrantToken,
    twoFactorMethod,
    shouldShowVerifyEmailModal,
    setShouldShowVerifyEmailModal,
  };
};
